import React from 'react';
import styled from 'styled-components';
import { COLORS } from '../../Styles/Colors';
import { FORM } from './formStyles';
import { FormField } from './FormField';

const UploadZone = styled.label<{ $hasFile?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? COLORS.teal : FORM.border)};
  border-radius: ${FORM.borderRadius};
  background-color: ${({ $hasFile }) => ($hasFile ? `${COLORS.teal}08` : FORM.surface)};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.teal};
    background-color: ${COLORS.teal}08;
  }

  input {
    display: none;
  }
`;

const UploadText = styled.span`
  color: ${FORM.text};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const UploadSubtext = styled.span`
  color: ${FORM.textMuted};
  font-size: ${FORM.hintSize};
  text-align: center;
`;

const PreviewWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-top: 12px;
`;

const PreviewImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: ${FORM.fieldRadius};
  border: 1px solid ${FORM.border};
`;

const RemoveButton = styled.button`
  padding: 6px 12px;
  font-size: ${FORM.hintSize};
  color: ${COLORS.red};
  background: ${COLORS.red}10;
  border: 1px solid ${COLORS.red}40;
  border-radius: ${FORM.fieldRadius};
  cursor: pointer;

  &:hover {
    background: ${COLORS.red}18;
  }
`;

interface FormFileUploadProps {
  label?: string;
  hint?: string;
  accept?: string;
  fileName?: string;
  placeholder?: string;
  subtext?: string;
  previewUrl?: string | null;
  icon?: React.ReactNode;
  onChange: (file: File | null) => void;
  onRemove?: () => void;
}

export const FormFileUpload: React.FC<FormFileUploadProps> = ({
  label,
  hint,
  accept = 'image/*',
  fileName,
  placeholder = 'Click to upload a file',
  subtext,
  previewUrl,
  icon,
  onChange,
  onRemove,
}) => (
  <FormField label={label} hint={hint}>
    <UploadZone $hasFile={!!fileName}>
      <input
        type="file"
        accept={accept}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {icon}
      <UploadText>{fileName || placeholder}</UploadText>
      {subtext && <UploadSubtext>{subtext}</UploadSubtext>}
    </UploadZone>
    {previewUrl && (
      <PreviewWrapper>
        <PreviewImage src={previewUrl} alt="Preview" />
        {onRemove && (
          <RemoveButton type="button" onClick={onRemove}>
            Remove
          </RemoveButton>
        )}
      </PreviewWrapper>
    )}
  </FormField>
);
